import { useEffect, useMemo, useState } from 'react';

import { LoadingButton } from '@mui/lab';
import {
    Alert,
    FormControl,
    FormControlLabel,
    FormLabel,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import { Box } from '@mui/system';

import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import { useTranslate } from 'src/locales';
import { useGetOrganizationSettingsQuery } from 'src/services/organisation/organisation.service';
import { OrganizationSetting } from 'src/services/organisation/organisation.types';
import { useUpdateInterviewPhasesMutation } from 'src/services/interviews/interviews.service';
import { useGetApplicationByIdQuery } from 'src/services/applications/applications.service';

import { ConfirmDialog } from 'src/components/custom-dialog';
import { enqueueSnackbar } from 'notistack';

type Props = {
    open: boolean;
    applicationId: string;
    onClose: VoidFunction;
    onSuccess?: VoidFunction;
};

export default function InterviewPhaseModal({ open, applicationId, onClose, onSuccess }: Props) {
    const { t } = useTranslate();
    const { organization } = useAuthContext();

    const [phaseSelectionMode, setPhaseSelectionMode] = useState<'next' | 'custom'>('next');
    const [selectedPhase, setSelectedPhase] = useState<number>(1);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>('');

    // Get organization settings to determine max interview phases
    const { currentData: settingsData } = useGetOrganizationSettingsQuery(
        organization.id
    );

    // Get application data to determine current phase
    const { data: applicationData, isLoading: isLoadingApplication } = useGetApplicationByIdQuery(applicationId, {
        skip: !open || !applicationId
    });

    // Update interview phase mutation
    const [updateInterviewPhase, { isLoading: isUpdating }] = useUpdateInterviewPhasesMutation();

    // Calculate max interview phases from organization settings
    const maxInterviewPhases = useMemo(() => {
        if (settingsData) {
            const interviewPhasesSetting = settingsData.find(
                (setting) => setting.setting_type === OrganizationSetting.MAX_INTERVIEW_PHASES
            );

            if (interviewPhasesSetting) {
                return interviewPhasesSetting.setting_value as number;
            }
        }

        return 5; // Default value if setting not found
    }, [settingsData]);

    // Current interview phase from application data
    const currentPhase = useMemo(() => applicationData?.interview_phase || 0, [applicationData]);

    // Check if current phase is already at max
    const isAtMaxPhase = useMemo(() => currentPhase >= maxInterviewPhases, [currentPhase, maxInterviewPhases]);

    // Calculate next phase (current + 1, but not exceeding max)
    const nextPhase = useMemo(() => Math.min(currentPhase + 1, maxInterviewPhases), [currentPhase, maxInterviewPhases]);

    // Generate phase options for the dropdown
    const phaseOptions = useMemo(() => {
        const options = [];
        for (let i = 1; i <= maxInterviewPhases; i++) {
            options.push({
                value: i,
                label: t('applications.interview_phase_modal.phase_number', { number: i })
            });
        }
        return options;
    }, [maxInterviewPhases, t]);

    // Reset form when modal opens
    useEffect(() => {
        if (open) {
            setPhaseSelectionMode('next');
            setErrorMessage('');
        }
    }, [open]);

    // Update selected phase when application data or selection mode changes
    useEffect(() => {
        if (phaseSelectionMode === 'next') {
            setSelectedPhase(nextPhase);
        }
    }, [nextPhase, phaseSelectionMode]);

    const handleSubmit = async () => {
        try {
            setIsSubmitting(true);
            setErrorMessage('');

            // Validate phase selection
            if (selectedPhase <= currentPhase) {
                setErrorMessage(t('applications.interview_phase_modal.phase_must_be_higher'));
                setIsSubmitting(false);
                return;
            }

            if (selectedPhase > maxInterviewPhases) {
                setErrorMessage(t('applications.interview_phase_modal.phase_exceeds_max', { max: maxInterviewPhases }));
                setIsSubmitting(false);
                return;
            }

            // Update the interview phase
            await updateInterviewPhase({
                applicationId,
                phase: selectedPhase
            }).unwrap();

            enqueueSnackbar(t('applications.api.update_interview_phase.success'), { variant: 'success' });

            // Call success callback if provided
            if (onSuccess) {
                onSuccess();
            }

            // Close the modal
            onClose();
        } catch (error) {
            console.error('Failed to update interview phase:', error);
            setErrorMessage(t('applications.api.update_interview_phase.default_error'));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <ConfirmDialog
            open={open}
            onClose={onClose}
            title={t('applications.interview_phase_modal.title')}
            content={
                <Stack spacing={3} sx={{ my: 2 }}>
                    {isLoadingApplication ? (
                        <Typography>{t('common.loading')}</Typography>
                    ) : (
                        <>
                            <Typography variant="body2">
                                {t('applications.interview_phase_modal.current_phase', { phase: currentPhase || 0 })}
                            </Typography>

                            {isAtMaxPhase && (
                                <Alert severity="warning">
                                    {t('applications.interview_phase_modal.already_at_max_phase', { max: maxInterviewPhases })}
                                </Alert>
                            )}

                            {
                                !isAtMaxPhase && (
                                    <>
                                        <FormControl component="fieldset">
                                            <FormLabel component="legend">{t('applications.interview_phase_modal.select_phase_mode')}</FormLabel>
                                            <RadioGroup
                                                value={phaseSelectionMode}
                                                onChange={(e) => setPhaseSelectionMode(e.target.value as 'next' | 'custom')}
                                            >
                                                <FormControlLabel
                                                    value="next"
                                                    control={<Radio />}
                                                    label={t('applications.interview_phase_modal.next_phase', { phase: nextPhase })}
                                                />
                                                <FormControlLabel
                                                    value="custom"
                                                    control={<Radio />}
                                                    label={t('applications.interview_phase_modal.custom_phase')}
                                                />
                                            </RadioGroup>
                                        </FormControl>

                                        {phaseSelectionMode === 'custom' && (
                                            <FormControl fullWidth>
                                                <InputLabel>{t('applications.interview_phase_modal.select_phase')}</InputLabel>
                                                <Select
                                                    value={selectedPhase}
                                                    onChange={(e) => setSelectedPhase(Number(e.target.value))}
                                                    label={t('applications.interview_phase_modal.select_phase')}
                                                >
                                                    {phaseOptions.map((option) => (
                                                        <MenuItem key={option.value} value={option.value}>
                                                            {option.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        )}

                                        {errorMessage && (
                                            <Alert severity="error">{errorMessage}</Alert>
                                        )}
                                    </>
                                )
                            }

                        </>
                    )}
                </Stack>
            }
            action={
                <LoadingButton
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    loading={isSubmitting || isUpdating}
                    disabled={isLoadingApplication || isAtMaxPhase}
                >
                    {t('common.update')}
                </LoadingButton>
            }
        />
    );
}